import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Check, ChevronRight, ChevronDown, Video, Apple, Droplet, Leaf, ShieldAlert, Flame, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Symptom tracking
const symptomCategories = [
  'Bloating', 'BMI score', 'Constipation', 'Energy levels',
  'Exercise (frequency 1-7)', 'Headaches', 'Heartburn', 'Indigestion',
  'Insomnia', 'Lethargy', 'Menopausal hot flashes', 'Mental clarity',
  'Mood swings', 'Motivation/willpower', 'Self confidence', 'Skin problems',
  'Sugar cravings', 'Tongue description (1-9)',
];

const symptomWeeks = ['beginning', 'week1', 'end-week1', 'end-week2', 'end-week3'];
const symptomWeekLabels = ['Beginning', 'Week 1', 'End Wk 1', 'End Wk 2', 'End Wk 3'];

// Cravings tracking
const cravingsCategories = [
  'Alcohol', 'Bread', 'Cakes', 'Cheese', 'Chocolate', 'Chips',
  'Cigarettes', 'Coffee/Tea', 'Fatty Foods', 'Fried Foods', 'Fruit',
  'Junk Food', 'Orange juice', 'Pasta', 'Potatoes', 'Red meat', 'Sugar', 'Water',
];

const cravingsWeeks = ['begin-week1', 'end-week1', 'end-week2', 'end-week3'];
const cravingsWeekLabels = ['Begin Wk 1', 'End Wk 1', 'End Wk 2', 'End Wk 3'];

// Program days
const programDays = [
  { id: 1, title: 'Introduction', subtitle: 'Why Detox & What to Expect', tier: 'intro' },
  { id: 2, title: 'Food Guide', subtitle: 'What to Eat & What to Avoid', tier: 'intro' },
  { id: 3, title: 'Progress Charts', subtitle: 'Track Your Transformation', tier: 'intro' },
  { id: 4, title: 'Liver Flush Recipe', subtitle: 'Weekly Liver Support Protocol', tier: 'intro' },
  ...Array.from({ length: 21 }, (_, i) => ({
    id: i + 5,
    title: `Day ${i + 1}`,
    subtitle: '',
    videoUrl: '',
    tier: 'full',
  })),
];

const sections = [
  { id: 'intro', title: 'Getting Started', days: programDays.slice(0, 4) },
  { id: 'week1', title: 'Week 1 (Days 1-7)', days: programDays.slice(4, 11) },
  { id: 'week2', title: 'Week 2 (Days 8-14)', days: programDays.slice(11, 18) },
  { id: 'week3', title: 'Week 3 (Days 15-21)', days: programDays.slice(18, 25) },
];

const FeelGoodDetoxPage = () => {
  const { user, hasAccess } = useAuth();
  const { trackView, trackVideoPlay, trackLinkClick, trackComplete, trackSectionClick } = useActivityTracker('feel-good-detox');
  const [selectedDay, setSelectedDay] = useState(null);

  const handleSelectDay = (day) => {
    setSelectedDay(day);
    trackView(day.id, day.title + (day.subtitle ? ` - ${day.subtitle}` : ''));
  };
  const [expandedSection, setExpandedSection] = useState('all');
  const [scores, setScores] = useState({});
  const [savedScores, setSavedScores] = useState(null);
  const [savingScores, setSavingScores] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [activeChart, setActiveChart] = useState('symptoms');

  const hasIntroAccess = hasAccess('feel-good-detox-intro') || hasAccess('feel-good-detox');
  const hasFullAccess = hasAccess('feel-good-detox');
  const isDayUnlocked = (day) => day.tier === 'intro' ? hasIntroAccess : hasFullAccess;

  useEffect(() => {
    if (user?.id) {
      fetch(`${BACKEND_URL}/api/user/progress-chart/${user.id}?program_id=feel-good-detox`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.scores) { setSavedScores(data.scores); setScores(data.scores); }
        })
        .catch(() => {});
    }
  }, [user]);

  const handleScoreChange = (key, value) => {
    setScores(prev => ({ ...prev, [key]: value }));
    setScoresSaved(false);
  };

  const saveScores = async () => {
    if (!user?.id) return;
    setSavingScores(true);
    try {
      await fetch(`${BACKEND_URL}/api/user/progress-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, user_email: user.email, program_id: 'feel-good-detox', scores }),
      });
      setSavedScores(scores);
      setScoresSaved(true);
      setTimeout(() => setScoresSaved(false), 3000);
    } catch (err) { console.error('Failed to save:', err); }
    setSavingScores(false);
  };

  if (!user) {
    return (
      <div className="bg-cream min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8"><ArrowLeft size={18} /><span className="font-body text-sm">Back to Programs</span></Link>
          <div className="text-center py-20">
            <Lock size={48} className="text-stone-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-forest mb-3">Sign In Required</h2>
            <p className="text-stone-500 font-body">Please sign in to access the 21-Day Feel Good Detox.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasIntroAccess && !hasFullAccess) {
    return (
      <div className="bg-cream min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8"><ArrowLeft size={18} /><span className="font-body text-sm">Back to Programs</span></Link>
          <div className="text-center py-20">
            <Lock size={48} className="text-stone-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-forest mb-3">Program Locked</h2>
            <p className="text-stone-500 font-body mb-6">Contact Jill to unlock access to the 21-Day Feel Good Detox.</p>
            <Link to="/contact" className="bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors">Get in Touch</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="feel-good-detox-page">
      {/* Header */}
      <section className="py-8 md:py-12 bg-gradient-to-b from-forest/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"><ArrowLeft size={18} /><span className="font-body text-sm">Back to Programs</span></Link>
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
              <Leaf size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-forest">21-Day Feel Good Detox</h1>
              <p className="text-stone-500 font-body text-sm">Reset, energize and revitalize mind, body and soul</p>
            </div>
          </div>
          {hasIntroAccess && !hasFullAccess && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 inline-flex items-center gap-2">
              <Lock size={14} className="text-amber-600" />
              <span className="text-amber-700 font-body text-sm">Intro access only — daily lessons require full program access</span>
            </div>
          )}
        </div>
      </section>

      {/* Main */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden sticky top-28">
                <div className="p-4 bg-emerald-600 text-cream">
                  <h3 className="font-heading text-lg">Program Outline</h3>
                  <p className="font-body text-xs text-cream/70 mt-1">21 days · Whole foods · Deep reset</p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {sections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() => setExpandedSection(expandedSection === section.id ? 'all' : section.id)}
                        className="w-full px-4 py-3 flex items-center justify-between bg-stone-50 border-b border-stone-100 hover:bg-stone-100 transition-colors"
                      >
                        <span className="font-body text-sm font-medium text-forest">{section.title}</span>
                        <ChevronDown size={16} className={`text-stone-400 transition-transform ${expandedSection === 'all' || expandedSection === section.id ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {(expandedSection === 'all' || expandedSection === section.id) && (
                          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                            {section.days.map((day) => {
                              const unlocked = isDayUnlocked(day);
                              return (
                                <button
                                  key={day.id}
                                  onClick={() => unlocked && handleSelectDay(day)}
                                  className={`w-full px-4 py-2.5 flex items-center gap-3 border-b border-stone-50 transition-colors text-left ${selectedDay?.id === day.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''} ${unlocked ? 'hover:bg-stone-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                  data-testid={`day-${day.id}`}
                                >
                                  {unlocked ? (
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                      <Check size={12} className="text-emerald-600" />
                                    </div>
                                  ) : (
                                    <Lock size={14} className="text-stone-300 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-body text-sm ${unlocked ? 'text-forest' : 'text-stone-400'}`}>{day.title}</p>
                                    {day.subtitle && <p className="font-body text-xs text-stone-400 truncate">{day.subtitle}</p>}
                                  </div>
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-2">
              {/* Welcome */}
              {!selectedDay && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-8 text-white">
                    <h2 className="font-heading text-3xl mb-3">Welcome to the 21-Day Feel Good Detox</h2>
                    <p className="text-white/80 font-body">Let's reset, energize and revitalize our mind, body and soul</p>
                  </div>
                  <div className="p-6">
                    <p className="text-stone-600 font-body leading-relaxed mb-4">
                      As our bodies become weighed down with toxins, they tend to become sluggish and a host of issues start to show up. A nutritional detoxification can spring life into organs, energy and vitality levels!
                    </p>
                    <p className="text-forest font-body font-medium">3 weeks to reset and feel the difference.</p>
                    <div className="mt-6">
                      <button onClick={() => setSelectedDay(programDays[0])} className="bg-emerald-500 text-white px-6 py-3 rounded-full font-body font-medium hover:bg-emerald-600 transition-colors flex items-center gap-2" data-testid="start-program-btn">
                        Get Started <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {selectedDay && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {/* Header */}
                  <div className="bg-emerald-600 p-4 flex items-center gap-3">
                    <button onClick={() => setSelectedDay(null)} className="text-cream/70 hover:text-cream"><ArrowLeft size={20} /></button>
                    <div>
                      <h2 className="font-heading text-xl text-cream">{selectedDay.title}</h2>
                      {selectedDay.subtitle && <p className="text-cream/60 font-body text-sm">{selectedDay.subtitle}</p>}
                    </div>
                  </div>

                  {/* Introduction (id 1) */}
                  {selectedDay.id === 1 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-10">
                        <h3 className="font-heading text-3xl text-forest mb-3">Why Do a Detox?</h3>
                        <p className="text-terracotta font-body text-lg italic">Good question!</p>
                      </div>

                      <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                        <p className="text-stone-600 font-body leading-relaxed">
                          As our bodies become weighed down with toxins, they tend to become sluggish. A host of issues start to show up: weight gain, difficulties losing weight, digestive issues, slowed metabolism, muscular tension, immunity issues, fat stores around the mid-section, low energy levels, chronic headaches, insomnia, hormonal imbalances, skin conditions, gut and digestion issues, bloating and bad breath — to name a few.
                        </p>
                        <p className="text-forest font-body font-medium mt-4 text-center">
                          A nutritional detoxification can spring life into organs, energy and vitality levels!
                        </p>
                      </div>

                      {/* Food Addictions */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-amber-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Food Addictions vs Cravings</h4>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                            Food addictions can be emotionally and physiologically addictive. Some food flavorings and dyes used in commercially produced processed food products are highly addictive and potentially dangerous — MSG, nitrates, trans-fats, sweeteners like aspartame all have addictive properties.
                          </p>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                              <p className="font-body font-medium text-emerald-700 text-sm mb-2">Cravings (Signals)</p>
                              <p className="text-stone-600 font-body text-xs">A craving can be a signal that we need certain nutrients. Craving orange juice may mean low Vitamin C. Craving beets may mean low folic acid or iron.</p>
                            </div>
                            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                              <p className="font-body font-medium text-red-700 text-sm mb-2">Addictions (Patterns)</p>
                              <p className="text-stone-600 font-body text-xs">Craving chips, ice cream, sweets, mac n cheese may be a sign of addiction to salt, sugar, or food additives — or an emotional addiction to food.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Organ Cleansing */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Clarifying the Organs</h4>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                            As we consume foods that contain toxins, organs get clogged up — much like a kitchen sink that drains slowly. Our natural detox system slows, metabolism slows, the liver, kidneys, stomach, intestines, pancreas, gall bladder — almost all organs become affected.
                          </p>
                          <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                            <p className="text-forest font-body italic text-sm">
                              As we get organs working more efficiently, hormones work better, waste removal works better, our natural detox system works better — everything works better!
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Tips */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                          <h4 className="font-heading text-xl text-forest">Tips for Success</h4>
                        </div>
                        <div className="p-6">
                          <div className="grid sm:grid-cols-2 gap-3">
                            {[
                              'Recruit family and friends into your process',
                              'Organization is key!',
                              'Remember flavour — herbs and spices keep it interesting',
                              'Herbal teas with honey can help quench cravings',
                              'Eat often with enough fat in each meal',
                              'Lots of water!',
                              '30 minutes of activity daily to move toxins',
                              'Keep breathing — this also moves toxins',
                              'Be prepared for side effects and note them',
                              'Eat approx half cooked, half raw food',
                              'Pre-cook eggs or leftovers for easy breakfasts',
                              'Appetite curbers: avocado, nuts, olive oil, fats, proteins',
                            ].map((tip, idx) => (
                              <div key={idx} className="flex gap-2">
                                <span className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0" />
                                <p className="text-stone-600 font-body text-sm">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Moments of Weakness */}
                      <div className="bg-terracotta/10 rounded-2xl p-6 mb-8">
                        <h4 className="font-heading text-xl text-forest mb-3">Moments of Weakness</h4>
                        <p className="text-stone-600 font-body text-sm leading-relaxed mb-3">
                          We all have moments of weakness! If you partake in non-cleansing foods, hop back on the wagon the next day. If you notice you tend to punish yourself for falling off plans, practice compassion for self and use that energy in a forward and productive manner.
                        </p>
                        <p className="text-forest font-body italic text-sm">
                          Acknowledge the days you have accomplished. Imagine what you would say to someone else — and apply those wise words to yourself.
                        </p>
                      </div>

                      <div className="text-center py-4">
                        <p className="font-heading text-2xl text-forest">Let's reset, energize and revitalize.</p>
                        <span className="text-3xl mt-2 block">🌿</span>
                      </div>
                    </div>
                  )}

                  {/* Food Guide (id 2) */}
                  {selectedDay.id === 2 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-8">
                        <h3 className="font-heading text-3xl text-forest mb-3">Food Guide</h3>
                        <p className="text-stone-500 font-body">Your complete guide to what to eat</p>
                      </div>

                      {/* Allowable Foods */}
                      <h4 className="font-heading text-2xl text-forest text-center mb-6">Allowable Foods</h4>

                      {/* Vegetables */}
                      <div className="mb-4 bg-white rounded-2xl border-2 border-emerald-200 overflow-hidden">
                        <div className="bg-emerald-500 px-6 py-4">
                          <h5 className="font-heading text-lg text-white">Vegetables & Salad</h5>
                          <p className="text-emerald-100 font-body text-sm">9 servings/day preferable (size of fist)</p>
                        </div>
                        <div className="p-6">
                          <div className="flex flex-wrap gap-2">
                            {['Artichoke', 'Asparagus', 'Bamboo shoots', 'Green beans', 'Broccoli', 'Cauliflower', 'Brussels sprouts', 'Cabbage (any)', 'Celery', 'Onions (all)', 'Radishes', 'Dark leafy greens', 'Field greens', 'Peas', 'Seaweed/dulse', 'All squashes', 'Water chestnuts', 'Root veg (beets, carrots, sweet potato, parsnip, turnips)'].map((item) => (
                              <span key={item} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-body border border-emerald-200">{item}</span>
                            ))}
                          </div>
                          <p className="text-emerald-600 font-body text-xs mt-3 italic">Lemons and limes are accepted fruits!</p>
                        </div>
                      </div>

                      {/* Proteins */}
                      <div className="mb-4 bg-white rounded-2xl border-2 border-blue-200 overflow-hidden">
                        <div className="bg-blue-500 px-6 py-4">
                          <h5 className="font-heading text-lg text-white">Protein & Eggs</h5>
                          <p className="text-blue-100 font-body text-sm">2-4 servings/day (size of palm)</p>
                        </div>
                        <div className="p-6">
                          <div className="flex flex-wrap gap-2">
                            {['Eggs (3-4x/week, free range)', 'Chicken (free range)', 'Turkey', 'Cornish hen', 'Local Bermuda fish', 'Wild-caught fish', 'Wild salmon', 'Trout', 'Mackerel', 'Arctic Char', 'Any small white fish'].map((item) => (
                              <span key={item} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-body border border-blue-200">{item}</span>
                            ))}
                          </div>
                          <p className="text-blue-600 font-body text-xs mt-3 italic">You can't overeat REAL FOOD — go for it, overeat carrots and sweet potatoes!</p>
                        </div>
                      </div>

                      {/* Starches & Grains */}
                      <div className="mb-4 bg-white rounded-2xl border-2 border-amber-200 overflow-hidden">
                        <div className="bg-amber-500 px-6 py-4">
                          <h5 className="font-heading text-lg text-white">Starches, Grains, Nuts & Seeds</h5>
                          <p className="text-amber-100 font-body text-sm">3 servings/day (size of fist)</p>
                        </div>
                        <div className="p-6">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="font-body font-medium text-forest text-sm mb-2">Grains</p>
                              <div className="flex flex-wrap gap-1">
                                {['Brown rice', 'Quinoa', 'Buckwheat', 'Millet', 'Amaranth', 'Wild rice', 'GF Oats'].map((item) => (
                                  <span key={item} className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-xs font-body">{item}</span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="font-body font-medium text-forest text-sm mb-2">Nuts & Seeds</p>
                              <div className="flex flex-wrap gap-1">
                                {['Almonds', 'Cashews', 'Walnuts', 'Sunflower', 'Pumpkin', 'Sesame', 'Chia', 'Hemp', 'Coconut'].map((item) => (
                                  <span key={item} className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-xs font-body">{item}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-amber-600 font-body text-xs mt-3 italic">Peanuts not recommended — the fatty acid can be highly inflammatory.</p>
                        </div>
                      </div>

                      {/* Fats, Liquids, Cheese, Herbs/Spices, Sweet things */}
                      <div className="mb-4 bg-white rounded-2xl border-2 border-violet-200 overflow-hidden">
                        <div className="bg-violet-500 px-6 py-4">
                          <h5 className="font-heading text-lg text-white">Fats, Liquids & Extras</h5>
                        </div>
                        <div className="p-6 space-y-4">
                          <div>
                            <p className="font-body font-medium text-forest text-sm mb-2">Fats (organic, dark glass bottles)</p>
                            <p className="text-stone-500 font-body text-xs">Coconut oil (cold-pressed), extra-virgin olive oil, organic unsalted butter/ghee, hemp oil, flax oil, avocado oil. Avoid canola, corn, and most nut/seed oils.</p>
                          </div>
                          <div>
                            <p className="font-body font-medium text-forest text-sm mb-2">Liquids (min 2L/day)</p>
                            <p className="text-stone-500 font-body text-xs">Filtered water 2-3 litres, green tea, herbal/detox teas, milk thistle, dandelion, green drinks, fresh pressed vegetable juice, aloe vera juice.</p>
                          </div>
                          <div>
                            <p className="font-body font-medium text-forest text-sm mb-2">Cheese & Dairy (limited 1-2/day)</p>
                            <p className="text-stone-500 font-body text-xs">Buffalo mozzarella, goat cheese, feta, ricotta, cottage cheese, live yogurt. Avoiding commercially produced bovine products.</p>
                          </div>
                          <div>
                            <p className="font-body font-medium text-forest text-sm mb-2">Sweet Things (limited 1-2 tsp/day)</p>
                            <p className="text-stone-500 font-body text-xs">Honey, stevia, allulose, dates, fresh coconut, bananas, pineapple.</p>
                          </div>
                        </div>
                      </div>

                      {/* Juicing Guide */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                          <div className="flex items-center gap-3">
                            <Droplet size={20} className="text-emerald-600" />
                            <h4 className="font-heading text-xl text-forest">Juicing Guide</h4>
                          </div>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                            Juicing is the quickest, most effective way of providing the body with all the vitamins, minerals and enzymes essential for good health.
                          </p>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {[
                              { label: 'Bloating & flatulence', items: 'ginger, cumin, fennel, celery' },
                              { label: 'Cleansing liver', items: 'carrot, beetroot, celery, ginger' },
                              { label: 'Constipation', items: 'celery' },
                              { label: 'Detoxification', items: 'wheatgrass' },
                              { label: 'Headaches', items: 'celery, carrot, parsley, spinach, coconut milk' },
                              { label: 'Improving digestion', items: 'beetroot, ginger, carrot' },
                              { label: 'Improving immunity', items: 'carrot, ginger, garlic, wheatgrass' },
                              { label: 'Kidney support', items: 'celery, pomegranate, cranberry' },
                              { label: 'Overweight support', items: 'beet greens, parsley, celery, spinach' },
                            ].map((juice) => (
                              <div key={juice.label} className="bg-stone-50 rounded-lg p-3">
                                <p className="font-body font-medium text-forest text-xs">{juice.label}</p>
                                <p className="text-stone-500 font-body text-xs">{juice.items}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Detox Rules — moved to bottom */}
                      <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white">
                        <h4 className="font-heading text-xl mb-4 text-terracotta">The Detox Rules — What to Avoid</h4>
                        <div className="space-y-2">
                          {[
                            'No red meat, pork, shellfish',
                            'No dairy (except yogurt, preferably from health food store)',
                            'No fruit — except bananas, lemon, lime',
                            'No wheat or gluten',
                            'No alcohol',
                            'No biscuits, cakes, or doughnuts',
                            'No jams, spreads — except what is listed',
                            'No coffee, decaf coffee, tea (green and herbal OK)',
                            'No artificially produced flavourings, ketchup, vinegar, mustards',
                            'No large fish like tuna or swordfish (local Bermuda fish OK)',
                            'No nightshades — tomatoes, potatoes, peppers, eggplant, cucumber',
                            'No mushrooms',
                          ].map((rule, idx) => (
                            <div key={idx} className="flex gap-3 items-start">
                              <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                              <p className="text-cream/90 font-body text-sm">{rule}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress Charts (id 3) */}
                  {selectedDay.id === 3 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-6">
                        <h3 className="font-heading text-3xl text-forest mb-3">Progress Charts</h3>
                        <p className="text-stone-500 font-body text-sm">Score 1-5 at the beginning of each week</p>
                        <div className="flex flex-wrap justify-center gap-2 mt-3">
                          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-body">1 = Very frequent/severe</span>
                          <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-body">2 = Regular/bad</span>
                          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-body">3 = Fairly regular</span>
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-body">4 = Infrequent</span>
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-body">5 = Rare/minor</span>
                        </div>
                      </div>

                      {/* Chart Toggle */}
                      <div className="flex gap-2 mb-6">
                        <button
                          onClick={() => setActiveChart('symptoms')}
                          className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${activeChart === 'symptoms' ? 'bg-emerald-500 text-white' : 'bg-stone-100 text-forest hover:bg-stone-200'}`}
                          data-testid="symptoms-chart-tab"
                        >
                          Symptom Tracker
                        </button>
                        <button
                          onClick={() => setActiveChart('cravings')}
                          className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${activeChart === 'cravings' ? 'bg-emerald-500 text-white' : 'bg-stone-100 text-forest hover:bg-stone-200'}`}
                          data-testid="cravings-chart-tab"
                        >
                          Cravings Tracker
                        </button>
                      </div>

                      {/* Symptoms Chart */}
                      {activeChart === 'symptoms' && (
                        <div className="overflow-x-auto mb-6">
                          <table className="w-full border-collapse" data-testid="symptom-chart">
                            <thead>
                              <tr className="bg-emerald-600 text-cream">
                                <th className="px-3 py-2.5 text-left font-heading text-xs rounded-tl-lg">Symptom</th>
                                {symptomWeekLabels.map((label, i) => (
                                  <th key={i} className={`px-2 py-2.5 text-center font-heading text-xs ${i === symptomWeekLabels.length - 1 ? 'rounded-tr-lg' : ''}`}>{label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {symptomCategories.map((symptom, idx) => (
                                <tr key={symptom} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                  <td className="px-3 py-2 font-body text-xs text-forest">{symptom}</td>
                                  {symptomWeeks.map((week) => (
                                    <td key={week} className="px-2 py-2 text-center">
                                      <select
                                        value={scores[`s-${symptom}-${week}`] || ''}
                                        onChange={(e) => handleScoreChange(`s-${symptom}-${week}`, e.target.value)}
                                        className="w-12 px-1 py-1 rounded border border-stone-200 text-xs font-body text-center bg-white focus:ring-2 focus:ring-emerald-300"
                                      >
                                        <option value="">-</option>
                                        {[1,2,3,4,5].map(v => <option key={v} value={v}>{v}</option>)}
                                      </select>
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Cravings Chart */}
                      {activeChart === 'cravings' && (
                        <div className="overflow-x-auto mb-6">
                          <p className="text-stone-500 font-body text-sm mb-4">Tick the foods you most crave during each week. See how your body's demands change over the course of the program.</p>
                          <table className="w-full border-collapse" data-testid="cravings-chart">
                            <thead>
                              <tr className="bg-emerald-600 text-cream">
                                <th className="px-3 py-2.5 text-left font-heading text-xs rounded-tl-lg">Craving</th>
                                {cravingsWeekLabels.map((label, i) => (
                                  <th key={i} className={`px-2 py-2.5 text-center font-heading text-xs ${i === cravingsWeekLabels.length - 1 ? 'rounded-tr-lg' : ''}`}>{label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {cravingsCategories.map((craving, idx) => (
                                <tr key={craving} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                  <td className="px-3 py-2 font-body text-xs text-forest">{craving}</td>
                                  {cravingsWeeks.map((week) => (
                                    <td key={week} className="px-2 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={!!scores[`c-${craving}-${week}`]}
                                        onChange={(e) => handleScoreChange(`c-${craving}-${week}`, e.target.checked ? '1' : '')}
                                        className="w-4 h-4 rounded border-stone-300 text-emerald-500 focus:ring-emerald-300"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <button
                          onClick={saveScores}
                          disabled={savingScores}
                          className="bg-emerald-500 text-white px-6 py-3 rounded-full font-body font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                          data-testid="save-scores-btn"
                        >
                          {savingScores ? 'Saving...' : scoresSaved ? <><Check size={16} /> Saved!</> : 'Save My Progress'}
                        </button>
                        {savedScores && <span className="text-stone-400 font-body text-sm">Last saved progress loaded</span>}
                      </div>
                    </div>
                  )}

                  {/* Liver Flush Protocol (id 4) */}
                  {selectedDay.id === 4 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Droplet size={28} className="text-emerald-600" />
                        </div>
                        <h3 className="font-heading text-3xl text-forest mb-3">Liver Flush Protocol</h3>
                        <p className="text-stone-500 font-body">Weekly liver support for deep cleansing</p>
                      </div>

                      <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                        <p className="text-stone-600 font-body leading-relaxed">
                          The liver is your body's primary detox organ. During the 21-day program, performing a weekly liver flush helps support and accelerate the cleansing process. This simple drink is best taken first thing in the morning on an empty stomach, once per week throughout the detox.
                        </p>
                      </div>

                      <div className="mb-8 bg-white rounded-2xl border-2 border-emerald-200 overflow-hidden">
                        <div className="bg-emerald-500 px-6 py-4">
                          <h4 className="font-heading text-lg text-white">Liver Flush Recipe</h4>
                        </div>
                        <div className="p-6">
                          <div className="grid sm:grid-cols-2 gap-6 mb-6">
                            <div>
                              <p className="font-body font-medium text-forest text-sm mb-3">Ingredients</p>
                              <div className="space-y-2">
                                {[
                                  'Juice of 1 fresh lemon',
                                  '1 tablespoon cold-pressed olive oil',
                                  '1 small clove of garlic (crushed)',
                                  'Small piece of fresh ginger (grated)',
                                  '1 pinch of cayenne pepper',
                                  '250ml warm filtered water',
                                ].map((item, idx) => (
                                  <div key={idx} className="flex gap-2 items-start">
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0" />
                                    <p className="text-stone-600 font-body text-sm">{item}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="font-body font-medium text-forest text-sm mb-3">Instructions</p>
                              <div className="space-y-2">
                                {[
                                  'Squeeze lemon juice into a glass',
                                  'Add warm (not hot) water',
                                  'Stir in olive oil, crushed garlic, and grated ginger',
                                  'Add a pinch of cayenne pepper',
                                  'Drink first thing in the morning on an empty stomach',
                                  'Wait 30 minutes before eating breakfast',
                                ].map((step, idx) => (
                                  <div key={idx} className="flex gap-2 items-start">
                                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                                    <p className="text-stone-600 font-body text-sm">{step}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
                        <div className="flex items-start gap-3">
                          <ShieldAlert size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h5 className="font-body font-medium text-amber-800 text-sm mb-2">Important Notes</h5>
                            <ul className="space-y-1.5">
                              {[
                                'Perform the liver flush once per week during the 21-day program.',
                                'You may feel mild nausea — this is normal and a sign the flush is working.',
                                'Stay hydrated throughout the day after flushing.',
                                'If you have gallstones or a medical condition, consult your healthcare provider first.',
                              ].map((note, idx) => (
                                <li key={idx} className="text-stone-600 font-body text-sm flex gap-2">
                                  <span className="text-amber-500">•</span> {note}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-terracotta/10 rounded-2xl p-6 text-center">
                        <p className="text-forest font-body italic text-sm">
                          The liver flush is a powerful tool in your cleansing toolkit. Combined with the detox food plan, it helps your body clear out stored toxins and supports renewed energy and vitality.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Generic Day Content (Days 1-21) */}
                  {selectedDay.id >= 5 && (
                    <div className="p-6 md:p-8">
                      <div className="text-center py-16">
                        <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Video size={32} className="text-stone-400" />
                        </div>
                        <h3 className="font-heading text-xl text-forest mb-2">{selectedDay.title}</h3>
                        <p className="text-stone-500 font-body">Content coming soon</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeelGoodDetoxPage;
